import moment from 'moment';
import {
  keys,
  reduce,
  flatten,
  pipe,
  isEmpty,
  test,
  includes,
  either,
  __,
  path,
  concat,
} from 'ramda';
import { reduceIndex, mapIndex } from 'utilities/ramda';
import { OBSERVATION } from 'constants/resourceType';
import { REGISTERED } from 'constants/observationStatuses';
import {
  DETAILED_EP_REPORT_FORM_FIELDS,
  EDIT_DEVICE_INFO_FORM_FIELDS,
  ATRIAL_DEPENDENT_FIELD_VALUES,
  VENTRICULAR_DEPENDENT_FIELD_VALUES,
} from 'constants/forms';

export const getObservationShockValues = shocks => {
  return reduceIndex(
    (result, shock, index) => {
      return [
        ...result,
        ...reduce(
          (acc, shockKey) => {
            if (!shock[shockKey]) {
              return acc;
            }
            if (shockKey === 'shockDate') {
              return [
                ...acc,
                {
                  code: {
                    coding: [
                      {
                        code: `shockDate_${index}`,
                        display: 'Shock Date',
                      },
                    ],
                  },
                  valueDateTime: moment(shock[shockKey], ['MM/DD/YYYY']).format('YYYY-MM-DD'),
                },
              ];
            }
            return [
              ...acc,
              {
                code: {
                  coding: [
                    {
                      code: `shockHour_${index}`,
                      display: 'Shock Hour',
                    },
                  ],
                },
                valueTime: moment(shock[shockKey], ['HH:mm']).format('HH:mm:ss'),
              },
            ];
          },
          [],
          keys(shock),
        ),
      ];
    },
    [],
    shocks,
  );
};

export const isComponentCodeDetailedReportFormField = id =>
  includes(id, DETAILED_EP_REPORT_FORM_FIELDS) || test(/^lead_active_(\d)/, id);

export const removeAtrialAdditionalValues = (key, atrialRadioValue) =>
  !(atrialRadioValue === 'No' && ATRIAL_DEPENDENT_FIELD_VALUES.includes(key));

export const removeVentricularAdditionalValues = (key, ventricularRadioValue) =>
  !(ventricularRadioValue === 'No' && VENTRICULAR_DEPENDENT_FIELD_VALUES.includes(key));

export const isComponentCodeEditDeviceInfoFormField = pipe(
  path(['code', 'coding', '0', 'code']),
  either(includes(__, EDIT_DEVICE_INFO_FORM_FIELDS), test(/(^shockDate_(\d))|(^shockHour_(\d))/)),
);

export const getDetailedReportObservationValue = (value, key) => {
  if (Array.isArray(value)) {
    return pipe(
      mapIndex((subFields, index) =>
        reduce(
          (acc, k) => [
            ...acc,
            {
              code: {
                coding: [
                  {
                    code: test(/.+(_num|alert)$/, k)
                      ? `${key}_${index + 1}_num`
                      : `${key}_${index + 1}`, // TODO: now just for `lead_active` `_num` !!!
                    display: k,
                  },
                ],
              },
              ...(!isEmpty(subFields[k]) && { valueString: subFields[k] }),
            },
          ],
          [],
          keys(subFields),
        ),
      ),
      flatten,
    )(value);
  }

  return [
    {
      code: {
        coding: [
          {
            code: key,
            display: key,
          },
        ],
      },
      ...(typeof value === 'boolean' && value
        ? { valueString: 'Yes' }
        : !isEmpty(value) && typeof value !== 'boolean' && { valueString: value }),
    },
  ];
};

export const getObservationValue = (inputValue, inputProp) => {
  const rules = {
    sentOnDate: {
      code: 'sent_on_date',
      display: 'Sent on date',
      propValue: 'valueDateTime',
      getValue: val => val && moment(val, ['MM/DD/YYYY']).format('YYYY-MM-DD'),
    },
    sentOnHour: {
      code: 'sent_on_time',
      display: 'Sent on time',
      propValue: 'valueTime',
      getValue: val => val && moment(val, ['HH:mm']).format('HH:mm:ss'),
    },
    alert: {
      code: 'alert',
      display: 'Alert',
      propValue: 'valueString',
      getValue: val => (val === true ? 'alert found' : 'alert not found'),
    },
    batteryLife: {
      code: 'batteryLife',
      display: 'Battery life',
      propValue: 'valueString',
      getValue: val => val,
    },
    pacing: {
      code: 'pacing',
      display: 'Pacing',
      propValue: 'valueString',
      getValue: val => val,
    },
    shock: {
      code: 'shock',
      display: 'Shock',
      propValue: 'valueString',
      getValue: val => val,
    },
    deviceClinicEmail: {
      code: 'deviceClinicEmail',
      display: 'Device clinic email',
      propValue: 'valueString',
      getValue: val => val,
    },
  };

  const foundRule = rules[inputProp];
  const foundValue = foundRule ? foundRule.getValue(inputValue) : null;
  if (foundValue) {
    return [
      {
        code: {
          coding: [
            {
              code: foundRule.code,
              display: foundRule.display,
            },
          ],
        },
        [foundRule.propValue]: foundValue,
      },
    ];
  }
  return [];
};

export const createReportObservationBody = ({
  patientId,
  practitioners,
  componentData,
  reportId,
  docs,
}) => ({
  resourceType: OBSERVATION,
  status: REGISTERED,
  subject: {
    reference: `Patient/${patientId}`,
  },
  code: {
    coding: [
      {
        code: `patient_report`,
        display: 'Patient report',
      },
    ],
  },
  effectiveDateTime: new Date(),
  component: concat(
    [
      ...componentData,
      {
        code: {
          coding: [
            {
              code: `pdf_report`,
              display: 'Report file',
            },
          ],
        },
        valueAttachment: {
          url: `DocumentReference/${reportId}`,
        },
      },
    ],
    String(docs)
      ? [
          {
            code: {
              coding: [
                {
                  code: `additionalDocuments`,
                  display: 'Additional documents',
                },
              ],
            },
            valueString: String(docs),
          },
        ]
      : [],
  ),
  performer: practitioners,
});
